import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackWhatsAppClick, trackCallClick, trackCtaClick } from '../../utils/gtm';

// Compute days remaining until the filing deadline (Sep 30 2026)
const computeDaysRemaining = () => {
  const deadline = new Date('2026-09-30T23:59:59');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

const Header = ({ onOpenModal }) => {
  const location = useLocation();
  const isCorporateTax = location.pathname === '/corporate-tax';

  // State for Corporate Tax sticky header behavior
  const [isStuck, setIsStuck] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Live days remaining — recomputes on every render (page reload = fresh value)
  const daysRemaining = computeDaysRemaining();

  useEffect(() => {
    if (!isCorporateTax) return;

    const handleScroll = () => {
      setIsStuck(window.scrollY > 8);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isCorporateTax]);

  // Variant 1: Corporate Tax Header (DO NOT MODIFY)
  if (isCorporateTax) {
    return (
      <>
        {/* Topbar */}
        <div className="topbar">
          <div className="shell">
            <span>FTA Corporate Tax registration and return filing, Dubai</span>
            <div className="tb-right">
              <a href="tel:+971542119784" style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block' }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                +971 54 211 9784
              </a>
            </div>
          </div>
        </div>

        {/* Sticky Site Header */}
        <header className={`site-header ${isStuck ? 'is-stuck' : ''}`} id="siteHeader">
          <div className="shell">
            <nav className="nav" aria-label="Primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <a className="brand" href="#top" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                <img
                  src="/standard-auditing-logo-1.webp"
                  alt="Standard Auditing"
                  width="149"
                  height="45"
                  style={{ height: '42px', width: 'auto', display: 'block' }}
                  decoding="async"
                />
              </a>

              <div className="nav-links" style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                <a href="#register" style={{ color: 'var(--navy-700)', textDecoration: 'none', fontWeight: 600, fontSize: '.92rem' }}>Do I register?</a>
                <a href="#pricing" style={{ color: 'var(--navy-700)', textDecoration: 'none', fontWeight: 600, fontSize: '.92rem' }}>Cost</a>
                <a href="#process" style={{ color: 'var(--navy-700)', textDecoration: 'none', fontWeight: 600, fontSize: '.92rem' }}>Process</a>
                <a href="#penalties" style={{ color: 'var(--navy-700)', textDecoration: 'none', fontWeight: 600, fontSize: '.92rem' }}>Penalties</a>
                <a href="#faq" style={{ color: 'var(--navy-700)', textDecoration: 'none', fontWeight: 600, fontSize: '.92rem' }}>FAQ</a>
              </div>

              <div className="nav-cta" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button className="btn js-open-modal" onClick={onOpenModal}>
                  Book a free review
                </button>
                <button
                  className="burger"
                  id="burger"
                  aria-label="Open menu"
                  aria-expanded={isDrawerOpen}
                  aria-controls="drawer"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  <span></span>
                </button>
              </div>
            </nav>
          </div>
        </header>

        {/* Mobile Drawer menu */}
        <div className={`drawer ${isDrawerOpen ? 'is-open' : ''}`} id="drawer" aria-hidden={!isDrawerOpen}>
          <div className="drawer-top">
            <button className="burger" onClick={() => setIsDrawerOpen(false)} aria-label="Close menu" aria-expanded="true">
              <span></span>
            </button>
          </div>
          <a className="d-link" href="#register" onClick={() => setIsDrawerOpen(false)}>Do I have to register?</a>
          <a className="d-link" href="#pricing" onClick={() => setIsDrawerOpen(false)}>What it costs</a>
          <a className="d-link" href="#process" onClick={() => setIsDrawerOpen(false)}>How the process works</a>
          <a className="d-link" href="#penalties" onClick={() => setIsDrawerOpen(false)}>Late penalties</a>
          <a className="d-link" href="#compare" onClick={() => setIsDrawerOpen(false)}>Doing it yourself</a>
          <a className="d-link" href="#about" onClick={() => setIsDrawerOpen(false)}>About Standard Auditing</a>
          <a className="d-link" href="#faq" onClick={() => setIsDrawerOpen(false)}>FAQ</a>
          <button
            className="btn btn--lg"
            onClick={() => {
              setIsDrawerOpen(false);
              onOpenModal();
            }}
            style={{ textTransform: 'none' }}
          >
            Book a free review
          </button>
          <a className="btn btn--ghost btn--lg" href="tel:+971542119784">Call +971 54 211 9784</a>
        </div>
      </>
    );
  }

  // Variant 2: Audit Tax Consulting Luxury Header (Matching uploaded screenshot colors exactly)
  return (
    <>
      {/* Top Running Deadline Ticker (Screenshot design) */}
      <div className="atc-ticker" aria-label="Urgent deadline countdown">
        <div className="atc-ticker-inner">
          <div className="atc-ticker-item">
            <span className="atc-ticker-text">Corporate Tax filing deadline: <strong className="atc-ticker-date">30 September 2026</strong></span>
            <span className="atc-ticker-bullet">•</span>
            <span className="atc-ticker-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{daysRemaining} {daysRemaining === 1 ? 'DAY' : 'DAYS'} REMAINING</span>
            </span>
          </div>

          <div className="atc-ticker-item">
            <span className="atc-ticker-text">Corporate Tax filing deadline: <strong className="atc-ticker-date">30 September 2026</strong></span>
            <span className="atc-ticker-bullet">•</span>
            <span className="atc-ticker-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{daysRemaining} {daysRemaining === 1 ? 'DAY' : 'DAYS'} REMAINING</span>
            </span>
          </div>

          <div className="atc-ticker-item">
            <span className="atc-ticker-text">Corporate Tax filing deadline: <strong className="atc-ticker-date">30 September 2026</strong></span>
            <span className="atc-ticker-bullet">•</span>
            <span className="atc-ticker-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{daysRemaining} {daysRemaining === 1 ? 'DAY' : 'DAYS'} REMAINING</span>
            </span>
          </div>

          <div className="atc-ticker-item">
            <span className="atc-ticker-text">Corporate Tax filing deadline: <strong className="atc-ticker-date">30 September 2026</strong></span>
            <span className="atc-ticker-bullet">•</span>
            <span className="atc-ticker-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{daysRemaining} {daysRemaining === 1 ? 'DAY' : 'DAYS'} REMAINING</span>
            </span>
          </div>
        </div>
      </div>

      <header className="atc-header" data-screen-label="Header">
        <div className="atc-container">
          <nav className="atc-header-nav" aria-label="Main Navigation">
            {/* Logo */}
            <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <img
                src="/standard-auditing-logo-1.webp"
                alt="Standard Auditing"
                className="atc-logo-img"
                width="149"
                height="45"
                fetchPriority="high"
                decoding="async"
              />
            </a>

            {/* Right Action Items Only */}
            <div className="atc-header-actions">
              {/* Phone */}
              <a
                href="tel:+971542119784"
                className="atc-phone-link gtm-phone-link"
                title="Call Standard Auditing"
                data-gtm-id="header-phone-link"
                id="headerPhoneLink"
                onClick={() => trackCallClick('header_phone')}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span>+971 54 211 9784</span>
              </a>

              {/* WhatsApp Button */}
              <a
                href="https://wa.me/971542119784"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('header_button')}
                className="atc-header-wa-btn gtm-whatsapp-btn"
                data-gtm-id="header-whatsapp-cta"
                id="headerWhatsappBtn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
                <span>WhatsApp</span>
              </a>

              {/* Get Expert Advice Button */}
              <button
                type="button"
                onClick={() => { trackCtaClick('header_get_expert_advice'); onOpenModal(); }}
                className="atc-header-cta-btn gtm-cta-btn"
                data-gtm-id="header-modal-cta"
                id="headerModalCtaBtn"
              >
                <span>Get expert advice</span>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
